/**
 * Cognito username + password auth wrappers (round-22 Phase D,
 * `public-website` spec + operator brief on user-management).
 *
 * Thin wrappers around `amazon-cognito-identity-js` so the SPA
 * can drive email/password sign-in AND self-service sign-up
 * against `dvgupv2-public-pool-{region}-{stage}` without going
 * through the Cognito Hosted UI. The Hosted UI still owns the
 * federated (Login-with-Amazon / Google / Facebook) round-trips
 * per `<SignInPage>`; this module carries the "or continue with
 * email" branch of the same page and the `<SignUpPage>` full-
 * account-creation flow.
 *
 * The three exports intentionally return primitives (strings +
 * numbers) rather than SDK objects so the SPA never leaks the
 * `amazon-cognito-identity-js` types past this file — a future
 * migration to a different Cognito client is a single-file swap.
 *
 * Function-length budget: every export stays under 30 lines per
 * Requirement 3.7 (slight relaxation over the 20-line default —
 * these functions each drive a discrete Cognito round-trip whose
 * shape is dictated by the SDK).
 */

import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';

/** Common inputs — every operation needs the pool id + app client id. */
export interface CognitoPoolArgs {
  readonly userPoolId: string;
  readonly userPoolClientId: string;
}

/** Successful sign-in payload the SPA persists into the auth slice. */
export interface SignInResult {
  readonly idToken: string;
  readonly accessToken: string;
  readonly refreshToken?: string;
  /** Seconds until the access window closes (matches Cognito's `expires_in`). */
  readonly expiresIn: number;
  /** Cognito `sub` claim — stable identifier for the profile row. */
  readonly cognitoSub: string;
}

/** Successful sign-up payload; the SPA routes to the verify-email page. */
export interface SignUpResult {
  readonly cognitoSub: string;
  /** True when Cognito flagged the account confirmed on creation. */
  readonly userConfirmed: boolean;
  /** Destination Cognito emailed the verification code to (usually the input email). */
  readonly codeDeliveryDestination?: string;
}

function makePool(args: CognitoPoolArgs): CognitoUserPool {
  return new CognitoUserPool({
    UserPoolId: args.userPoolId,
    ClientId: args.userPoolClientId,
  });
}

function extractSub(idToken: string): string {
  // Cognito ID tokens always carry a `sub` claim; the SPA's
  // decodeJwt would import a cycle back here, so decode inline.
  const [, payload] = idToken.split('.');
  if (!payload) return '';
  const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
  try {
    const parsed = JSON.parse(atob(b64 + pad)) as { readonly sub?: string };
    return parsed.sub ?? '';
  } catch {
    return '';
  }
}

function sessionToResult(session: CognitoUserSession): SignInResult {
  const idJwt = session.getIdToken().getJwtToken();
  const accessJwt = session.getAccessToken().getJwtToken();
  const refresh = session.getRefreshToken().getToken();
  const idExp = session.getIdToken().getExpiration(); // epoch seconds
  const nowSec = Math.floor(Date.now() / 1000);
  return {
    idToken: idJwt,
    accessToken: accessJwt,
    refreshToken: refresh.length > 0 ? refresh : undefined,
    expiresIn: Math.max(0, idExp - nowSec),
    cognitoSub: extractSub(idJwt),
  };
}

export interface SignInArgs extends CognitoPoolArgs {
  readonly email: string;
  readonly password: string;
}

/**
 * USER_PASSWORD_AUTH sign-in. Returns primitives the SPA persists
 * into the auth slice; the caller renders the resulting error
 * message directly (Cognito's messages are user-facing enough).
 */
export function signInWithEmailPassword(args: SignInArgs): Promise<SignInResult> {
  return new Promise((resolve, reject) => {
    const pool = makePool(args);
    const user = new CognitoUser({ Username: args.email, Pool: pool });
    // Uses `USER_SRP_AUTH` (the SDK default). Cognito's SRP flow
    // proves knowledge of the password without sending it in
    // cleartext, so the app-client only needs `userSrp: true`
    // enabled (`infra/src/stacks/core-infrastructure-stack.ts` —
    // `createPublicUserPoolClient`). Do NOT switch this to
    // `USER_PASSWORD_AUTH` — that would require also flipping
    // `userPassword: true` on the app-client, and SRP is the
    // stronger flow for public SPA clients.
    user.authenticateUser(
      new AuthenticationDetails({ Username: args.email, Password: args.password }),
      {
        onSuccess: (session) => resolve(sessionToResult(session)),
        onFailure: (err) => reject(err instanceof Error ? err : new Error(String(err))),
        newPasswordRequired: () => {
          reject(new Error('new-password-required'));
        },
      },
    );
  });
}

export interface SignUpArgs extends CognitoPoolArgs {
  readonly email: string;
  readonly password: string;
  /** Preferred gamertag; stored as the `custom:username` attribute. */
  readonly username: string;
  readonly firstName: string;
  readonly lastName: string;
  /** Optional E.164 phone number, e.g. `+14085551234`. */
  readonly phoneE164?: string;
}

/**
 * Self-service sign-up. Creates the Cognito user + records the
 * profile fields as Cognito attributes so a matching profile row
 * can be upserted after email verification. Cognito emails the
 * user a 6-digit code; the caller navigates to `/verify-email`
 * so the user can enter it.
 */
export function signUpWithEmailPassword(args: SignUpArgs): Promise<SignUpResult> {
  return new Promise((resolve, reject) => {
    const pool = makePool(args);
    const attrs: CognitoUserAttribute[] = [
      new CognitoUserAttribute({ Name: 'email', Value: args.email }),
      new CognitoUserAttribute({ Name: 'given_name', Value: args.firstName }),
      new CognitoUserAttribute({ Name: 'family_name', Value: args.lastName }),
      new CognitoUserAttribute({ Name: 'custom:username', Value: args.username }),
    ];
    if (args.phoneE164 !== undefined && args.phoneE164.length > 0) {
      attrs.push(new CognitoUserAttribute({ Name: 'phone_number', Value: args.phoneE164 }));
    }
    pool.signUp(args.email, args.password, attrs, [], (err, data) => {
      if (err !== undefined && err !== null) {
        reject(err instanceof Error ? err : new Error(String(err)));
        return;
      }
      if (data === undefined) {
        reject(new Error('signUp-no-result'));
        return;
      }
      resolve({
        cognitoSub: data.userSub,
        userConfirmed: data.userConfirmed,
        codeDeliveryDestination: data.codeDeliveryDetails?.Destination,
      });
    });
  });
}

export interface ConfirmSignUpArgs extends CognitoPoolArgs {
  readonly email: string;
  readonly code: string;
}

/**
 * Confirm the six-digit code Cognito emailed after
 * {@link signUpWithEmailPassword}. On success the account is
 * usable and the caller can immediately drive
 * {@link signInWithEmailPassword}.
 */
export function confirmSignUp(args: ConfirmSignUpArgs): Promise<void> {
  return new Promise((resolve, reject) => {
    const pool = makePool(args);
    const user = new CognitoUser({ Username: args.email, Pool: pool });
    user.confirmRegistration(args.code, true, (err) => {
      if (err !== undefined && err !== null) {
        reject(err instanceof Error ? err : new Error(String(err)));
        return;
      }
      resolve();
    });
  });
}

export interface ResendCodeArgs extends CognitoPoolArgs {
  readonly email: string;
}

/**
 * Ask Cognito to re-send the verification code — the
 * `<VerifyEmailPage>` "resend code" affordance calls this.
 */
export function resendConfirmationCode(args: ResendCodeArgs): Promise<void> {
  return new Promise((resolve, reject) => {
    const pool = makePool(args);
    const user = new CognitoUser({ Username: args.email, Pool: pool });
    user.resendConfirmationCode((err) => {
      if (err !== undefined && err !== null) {
        reject(err instanceof Error ? err : new Error(String(err)));
        return;
      }
      resolve();
    });
  });
}


export interface ChangePasswordArgs extends CognitoPoolArgs {
  readonly email: string;
  readonly oldPassword: string;
  readonly newPassword: string;
}

/**
 * Change the caller's Cognito password. Requires the current
 * password (Cognito authenticates first, then swaps in the new
 * password). Cognito enforces the pool's password policy so any
 * server-side rejection surfaces here as a thrown Error.
 */
export function changePassword(args: ChangePasswordArgs): Promise<void> {
  return new Promise((resolve, reject) => {
    const pool = makePool(args);
    const user = new CognitoUser({ Username: args.email, Pool: pool });
    user.authenticateUser(
      new AuthenticationDetails({ Username: args.email, Password: args.oldPassword }),
      {
        onSuccess: () => {
          user.changePassword(args.oldPassword, args.newPassword, (err) => {
            if (err !== undefined && err !== null) {
              reject(err instanceof Error ? err : new Error(String(err)));
              return;
            }
            resolve();
          });
        },
        onFailure: (err) => reject(err instanceof Error ? err : new Error(String(err))),
      },
    );
  });
}
